import { getFrontendConfigValue, logOutIntercomUser, pushToDataLayer, updateIntercomUser } from '@arnold/common';
import { ACCESS_TOKEN_NOT_FOUND_ERROR_MESSAGE, ENVIRONMENT, ProductCode } from '@arnold/core';
import { ComponentType, createContext, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router';
import {
  AccessTokenStatus,
  Features,
  IsAccessTokenValidQuery,
  useIsAccessTokenValidQuery,
  useOrganizationQuery,
  UserOrgRole,
} from '../generated/hooks';
import auth from '../lib/auth';
import ee from '../lib/eventEmitter';
import { getOrganizationName, setLoggedUser } from '../lib/helpers';
import storage from '../lib/storage';
import AuthorizedPage from './AuthorizedPage/AuthorizedPage';
import Loading from './Loading';
import { OrganizationProvider } from './providers/OrganizationProvider';

type Props = {
  search?: string;
  leftNavbarOnly?: boolean;
  hideSidebar?: boolean;
  isWithoutPadding?: boolean;
  Component: ComponentType<any>;
};

export const AuthorizedPageLoader: FC<Props> = ({ search, leftNavbarOnly, hideSidebar, Component, ...props }) => {
  const { i18n } = useTranslation('toast');
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const handleError = () => {
    // auth.removeUserCookies();
    logOutIntercomUser();
  };

  const handleSuccess = (responseData: IsAccessTokenValidQuery) => {
    if (responseData.isAccessTokenValid) {
      if (responseData.isAccessTokenValid.status !== AccessTokenStatus.Valid) {
        auth.removeUserCookies();
        ee.emitAccessTokenValidationModal(responseData.isAccessTokenValid.status || '');
      }

      // Consent is now implied at login; remove this block in the future.
      // if (!responseData.isAccessTokenValid.consent) {
      //   auth.removeUserCookies();
      //   ee.emitAccessTokenValidationModal(AccessTokenStatus.ConsentNotFound);
      // }
    }
  };

  const { data, loading, error } = useIsAccessTokenValidQuery({
    onCompleted: handleSuccess,
    onError: handleError,
  });

  const {
    data: organizationData,
    loading: organizationLoading,
    error: organizationError,
    refetch: refetchOrganizationQuery,
  } = useOrganizationQuery();

  useEffect(() => {
    setLoggedUser(data?.isAccessTokenValid.user);
    return () => {
      setLoggedUser(undefined);
    };
  }, [data?.isAccessTokenValid.user]);

  useEffect(() => {
    pushToDataLayer({ userId: data?.isAccessTokenValid.user?.id, orgId: organizationData?.organization?.id });
  }, [location, data?.isAccessTokenValid.user?.id, organizationData?.organization?.id]);

  useEffect(() => {
    const user = data?.isAccessTokenValid.user;
    const organization = organizationData?.organization;
    if (user && organization) {
      if (!storage.getDetectedLanguage()) {
        storage.setDetectedLanguage(organization.primaryLanguageCode);
        i18n.changeLanguage(organization.primaryLanguageCode);
      }
      const validPeriod = organization?.productVersion;

      const satismeter = (window as any).satismeter;
      if (
        getFrontendConfigValue('ENVIRONMENT') === ENVIRONMENT.PROD &&
        satismeter &&
        validPeriod?.validFrom &&
        validPeriod.validTo
      ) {
        const validFrom = new Date(validPeriod.validFrom);
        const validTo = new Date(validPeriod.validTo);
        const midDate = new Date((validFrom.getTime() + validTo.getTime()) / 2);
        satismeter({
          writeKey: getFrontendConfigValue('SATISMETER_WRITE_KEY'),
          userId: user.id,
          traits: {
            name: `${user.firstname} ${user.surname}`,
            contractHalf: midDate.toISOString(),
            contractValidFrom: validFrom.toISOString(),
            contractValidTo: validTo.toISOString(),
            createdAt: user.createdAt,
            plan: validPeriod.product,
            isPaidPlan: validPeriod.product !== ProductCode.Free,
            role: UserOrgRole.OrgAdmin,
            organizationName: organization.name,
            organizationId: organization.id,
            email: user.username,
          },
          language: i18n.language,
        });
      }

      updateIntercomUser({
        user_id: user.id,
        user_hash: auth.getIntercomHash(),
        marketingConsent: user.marketingConsent,
        name: `${user.firstname} ${user.surname}`,
        email: user.username,
        language_override: i18n.language,
        'Organization role': UserOrgRole.OrgAdmin,
        company: organization
          ? {
              id: organization.id,
              name: organization.name,
              'System name': organization.systemName,
              plan: validPeriod?.product,
              'Primary language': organization.primaryLanguageCode,
              'All languages': organization.languages.map(({ code }) => code).join(', '),
              'Company ID': organization.companyId,
              'Licence count': validPeriod?.licenceCount,
              'Valid from': validPeriod?.validFrom,
              'Valid to': validPeriod?.validTo,
            }
          : {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, organizationData]);

  if (loading || organizationLoading) {
    return <Loading />;
  }
  if (
    (data && data.isAccessTokenValid.status !== AccessTokenStatus.Valid) ||
    error?.name === 'AccessTokenNotFound' ||
    error?.message === ACCESS_TOKEN_NOT_FOUND_ERROR_MESSAGE
  ) {
    auth.removeUserCookies();
    const loginPath = '/login';
    return (
      <Redirect
        to={{
          pathname: loginPath,
          state: { from: location },
        }}
      />
    );
  }
  if (error || organizationError) {
    throw new Error(error?.message || organizationError?.message);
  }
  if (data && organizationData && data.isAccessTokenValid.user) {
    const organization = organizationData.organization;
    let orgName = '';
    if (organization && organization.config) {
      orgName = getOrganizationName(organization.systemName, organization.name);
    }
    return (
      <FeatureFlagsContext.Provider
        value={(organization?.featureNames?.filter((feature) => feature !== null) as Features[]) ?? []}
      >
        <AuthorizedPage
          organizationName={orgName}
          user={data.isAccessTokenValid.user}
          organizationId={organization?.id}
          organizationLicence={
            organization?.productVersion
              ? {
                  ...organization?.productVersion,
                  activeRespondentsInCurrentPeriodCount: organization.activeRespondentsInCurrentPeriodCount,
                }
              : null
          }
          allowedMoreOrganizations={data.isAccessTokenValid.user?.allowedMoreOrganizations || false}
          product={organization?.productVersion?.product}
          hasProcessSurveys={organizationData?.organization?.hasProcessSurveys || false}
          hasPulserModule={organization?.featureNames?.includes(Features.PulserModule) || false}
          leftNavbarOnly={leftNavbarOnly}
          hideSidebar={hideSidebar}
        >
          <OrganizationProvider organization={organization} refetch={refetchOrganizationQuery}>
            <Component
              {...props}
              user={data.isAccessTokenValid.user}
              organization={organizationData.organization}
              history={history}
              location={location}
              match={match}
            />
          </OrganizationProvider>
        </AuthorizedPage>
      </FeatureFlagsContext.Provider>
    );
  }

  return null;
};

export const FeatureFlagsContext = createContext<Features[]>([]);
